<template>
  <div class="p-page-create-edit" v-if="!loading">
    <page-title
      marginBottom="0"
      subTitle="Drag and drop elements into the main page to create your bespoke page layout"
    >
      <span v-if="editMode">Edit Page</span>
      <span v-else>Create Page</span>
    </page-title>

    <message v-if="duplicatePage" type="error">
      <h3>There's a problem</h3>
      <p>
        A page with the slug
        <strong>{{ duplicatePage.slug }}</strong> already exists.
      </p>
    </message>

    <message v-if="isDraft && page.status !== 'pending'" type="warning">
      <h3>You are editing a draft</h3>
      <p>
        The changes below are currently saved as a draft copy, and will not
        appear on the intranet until the page has been submitted.
      </p>
    </message>

    <message
      v-else-if="page.status === 'pending' && !this.$route.query.duplicate"
      type="warning"
    >
      <h3>You are editing a pending item</h3>
      <p>
        The changes below are currently pending review, and will not appear on
        the intranet until the page has been approved.
      </p>
    </message>

    <section-block :headerOverflowHidden="true">
      <template slot="header">
        <form-field
          dynamic-width
          ghost
          large
          min-width="312px"
          v-model="page.title"
          is-inline
          placeholder="Add your page title here..."
        ></form-field>
      </template>

      <template slot="actions">
        <dropdown class="u-show-tablet-down">
          <dropdown-action
            v-if="editMode && page.status == 'approved'"
            @click="onClickViewPage"
            >View Page</dropdown-action
          >

          <dropdown-action
            v-if="
              ['approved', 'rejected'].includes(page.status) === false &&
                editMode &&
                userCanApprove &&
                !isNew
            "
            @click="onClickApprove"
            :disabled="disabled"
            >Approve</dropdown-action
          >
          <dropdown-action
            v-if="
              ['approved', 'rejected'].includes(page.status) === false &&
                editMode &&
                userCanApprove &&
                !isNew
            "
            @click="onClickReject"
            :disabled="disabled"
            >Reject</dropdown-action
          >

          <dropdown-action
            @click="onClickSaveDraft"
            icon-before="save"
            :icon-before-fill="false"
            v-if="page.status !== 'pending'"
            :disabled="disabled"
            >Save Draft</dropdown-action
          >
          <dropdown-action
            v-if="editMode && isDraft && page.status !== 'pending'"
            @click="onClickDeleteDraft"
            icon-before="cross"
            danger-invert
            :disabled="disabled"
            >Delete Draft</dropdown-action
          >
          <dropdown-action
            v-if="editMode && userCanSubmit"
            primary="true"
            @click="onClickUpdate"
            iconBefore="edit"
            :disabled="disabled"
            >{{ isNew ? 'Submit' : 'Re-submit' }}</dropdown-action
          >
          <dropdown-action
            v-if="userCanSubmit && !editMode"
            primary="true"
            @click="onClickPublish"
            :disabled="disabled"
            >Submit</dropdown-action
          >
        </dropdown>
        <div class="u-hide-tablet">
          <base-button
            success
            v-if="
              ['approved', 'rejected'].includes(page.status) === false &&
                editMode &&
                userCanApprove &&
                !isNew
            "
            @click="onClickApprove"
            :disabled="disabled"
            >Approve</base-button
          >
          <base-button
            danger
            v-if="
              ['approved', 'rejected'].includes(page.status) === false &&
                editMode &&
                userCanApprove &&
                !isNew
            "
            @click="onClickReject"
            :disabled="disabled"
            >Reject</base-button
          >

          <base-button
            v-if="editMode && page.status == 'approved'"
            @click="onClickViewPage"
            >View Page</base-button
          >
          <base-button
            @click="onClickSaveDraft"
            icon-before="save"
            :icon-before-fill="false"
            v-if="page.status !== 'pending'"
            :disabled="disabled"
            >Save Draft</base-button
          >
          <base-button
            v-if="editMode && isDraft && page.status !== 'pending'"
            @click="onClickDeleteDraft"
            icon-before="cross"
            danger-invert
            :disabled="disabled"
            >Delete Draft</base-button
          >
          <base-button
            v-if="editMode && userCanSubmit"
            primary="true"
            @click="onClickUpdate"
            iconBefore="edit"
            :disabled="disabled"
            >{{ isNew ? 'Submit' : 'Re-submit' }}</base-button
          >
          <base-button
            v-if="userCanSubmit && !editMode"
            primary="true"
            @click="onClickPublish"
            :disabled="disabled"
            >Submit</base-button
          >
        </div>
      </template>

      <page-builder
        :pageId="page.id"
        :reviewDate="page.review_date"
        :interestgroups="page.interestgroups"
        @reviewDateUpdated="(newDate) => (page.review_date = newDate)"
        @interestGroupUpdated="onInterestGroupUpdated"
        @resetChildren="onResetChildren"
        v-if="dataLoaded"
      ></page-builder>
    </section-block>
    <modal
      v-if="rejectModalOpen"
      class="ta-center"
      show-close-button
      @close="rejectModalOpen = false"
    >
      <h1 slot="header">Reason for Rejection</h1>
      <form method="post" action v-on:submit.prevent="rejectPage">
        <form-field
          type="textarea"
          placeholder="Please specify a reason for rejection"
          v-model="rejectionReason"
          required
        />
        <base-button type="submit" :primary="true">Submit Reason</base-button>
      </form>
    </modal>
  </div>

  <loading v-else></loading>
</template>

<script>
import PageBuilder from '@/components/PageBuilder'
import UserHasPermission from '@/mixins/UserHasPermission'
import { mapState } from 'vuex'

const Dropdown = () => import('@/components/Dropdown')
const DropdownAction = () => import('@/components/DropdownAction')

export default {
  name: 'admin-page-edit-create',

  components: {
    PageBuilder,
    Dropdown,
    DropdownAction,
  },

  mixins: [UserHasPermission],

  data() {
    return {
      editMode: false,
      loading: false,
      duplicatePage: null,
      originalReviewDate: null,
      disabled: true,
      rejectModalOpen: false,
      rejectionReason: '',
      isDraft: false,
      draft: null,
      dataLoaded: false,
      changeChildOrder: false
    }
  },

  beforeRouteLeave(to, from, next) {
    if (this.elementsHaveChanged) {
      this.$store.dispatch('createDialog', {
        messageText:
          'Any changes you made will be lost if you navigate away from this page, are you sure?',
        confirm: () => {
          next()
        },
      })
    } else {
      next()
    }
  },

  created() {
    this.loading = true
    this.$store.commit('pages/setCurrentPage', {})
    this.$store.commit('pageElements/setOriginalPageElements', [])
    this.$store.commit('pageElements/setPageElements', [])

    const { pageId } = this.$route.params || null
    const { duplicate } = this.$route.query

    if (pageId) {
      this.editMode = true
      this.retrievePage()
    } else {
      this.loading = false
      this.disabled = false
      this.dataLoaded = true
    }

    if (duplicate) {
      this.loading = true
      this.retrievePage(duplicate, true)
    }
  },

  methods: {
    onResetChildren(val) {
      this.changeChildOrder = val;
    },
    /**
     * Extract the bits that we need here
     */
    async saveDraftPage(payload) {
      const {
        title,
        slug,
        elements,
        interestgroup_ids,
        parent_id,
        review_date,
        id,
      } = payload

      const menu_sort = this.changeChildOrder && this.childMenuIds ? this.childMenuIds.join(',') : null

      let draft

      try {
        draft = await this.$store.dispatch('pages/saveDraft', {
          title,
          slug,
          elements,
          interestgroup_ids,
          parent_id,
          review_date,
          menu_sort,
          pageId: id,
        })
        return Promise.resolve(draft)
      } catch (err) {
        this.$store.dispatch(
          'notifications/create',
          {
            body: `There was a problem saving the draft`,
            type: 'error',
          },
          { root: true }
        )

        return Promise.reject(err)
      }
    },
    onClickSaveDraft() {
      // save the content to a draft
      // we only need title, slug and elements
      // TODO: Not to include parent_id, review_date and interestgroups

      // if there is no draft already, we need to save the page first before saving
      if (!this.isDraft && !this.editMode) {
        this.onClickPublish(true)
        return
      }

      // interest groups need to be just ids
      if (!this.page.interestgroup_ids && this.page.interestgroups) {
        this.page.interestgroup_ids = this.page.interestgroups.map(
          (group) => group.id
        )
      }
      // then remove the interest group bit
      delete this.page.interestgroups

      this.saveDraftPage(this.page)
        .then((draft) => {
          this.dataLoaded = false
          this.retrievePage(this.page.id)
          this.$store.dispatch(
            'notifications/create',
            {
              body: `The draft has been saved`,
              type: 'success',
            },
            { root: true }
          )
        })
        .catch((err) => {
          this.dataLoaded = true
          this.$store.dispatch(
            'notifications/create',
            {
              body: `There was a problem saving the draft`,
              type: 'error',
            },
            { root: true }
          )
        })
    },
    rejectPage() {
      const vm = this
      // do the following, but post the rejection reason along with the id i guess
      this.$store
        .dispatch('pages/reject', {
          pageId: this.page.id,
          reason: this.rejectionReason,
        })
        .then((page) => {
          this.rejectionReason = ''
          this.rejectModalOpen = false
          this.retrievePage()
          vm.$store.dispatch(
            'notifications/create',
            {
              body: `The page was rejected`,
              type: 'success',
            },
            { root: true }
          )
        })
    },
    onInterestGroupUpdated(val) {
      this.page.interestgroup_ids = val.map((grp) => grp.id)
    },
    retrievePage(pageId, setDuplicate) {
      this.loading = true
      pageId = pageId || this.$route.params.pageId

      /**
       * We now need to check if there is a draft version of the page
       * If there is a draft, we need to load the data from the draft and display
       * This will only be the slug, title and elements though.
       *
       * Do this after loading the page so that we have all the meta available,
       * TODO: Think of the performance concerns, will we be getting too much data with 2 calls?
       */
      this.isDraft = false
      this.draft = {}

      this.$store
        .dispatch('pages/read', { pageSlugOrId: pageId, isAdmin: true })
        .then((page) => {
          this.loading = false

          this.originalReviewDate = page.review_date

          if (setDuplicate) {
            this.page.title += ' (Duplicated)'
            this.page.slug += '-duplicated'
          }

          this.$store.dispatch('pages/getMenuChildren', pageId)

          this.$store
            .dispatch('pages/getDraft', { pageId })
            .then((draft) => {
              this.isDraft = true
              this.page.title = draft.title
              this.page.slug = draft.slug
              this.page.review_date = draft.review_date
              this.draft = draft
              this.page.interestgroups = draft.interestgroups
              this.page.parent_id = draft.parent_id
              this.page.menu_sort = draft.menu_sort

              // need to now get meta info (parent, review_date, groups)
              this.page.parent_data = draft.parent_data

              // seperate things just to avoid confusion
              if (this.isDraft) {
                this.getDraftElements(pageId)
              } else {
                this.retrievePageElements(pageId)
              }
            })
            .catch((err) => {
              this.retrievePageElements(pageId)
            })

          // check for any children
        })
    },

    retrievePageElements(pageId) {
      pageId = pageId || this.$route.params.pageId

      this.$store
        .dispatch('pageElements/list', pageId)
        .then(() => {
          this.loading = false
          this.disabled = false
          this.dataLoaded = true
        })
        .catch(() => {
          this.loading = false
          this.disabled = false
          this.dataLoaded = true
        })
    },

    /**
     * This method is the same as above, but seperate out for now
     * just to avoid confusion
     */
    getDraftElements(pageId) {
      this.$store
        .dispatch('pageElements/listDraft', { pageId })
        .then(() => {
          this.loading = false
          this.disabled = false
          this.dataLoaded = true
        })
        .catch(() => {
          this.loading = false
          this.disabled = false
          this.dataLoaded = true
        })
    },

    async onClickApprove() {
      /**
       * If the review date is either in the past or is within the next 6 months, need to change
       * the date to be 12 months from today
       */
      const reviewDate = this.$dayjs(this.page.review_date).unix()
      const sixMonth = this.$dayjs()
        .add(6, 'month')
        .unix()

      if (reviewDate < sixMonth) {
        this.page.review_date = this.$dayjs()
          .add(12, 'month')
          .toString()
      }
      // if the page has a draft, we need to update the page before approval
      if (this.isDraft) {
        const pageElments = this.$store.getters['pageElements/forServer']
        const editedPage = JSON.parse(JSON.stringify(this.page))

        // add elements to the current page.
        editedPage.elements = pageElments

        // interest groups need to be just ids
        if (!editedPage.interestgroup_ids && editedPage.interestgroups) {
          editedPage.interestgroup_ids = editedPage.interestgroups.map(
            (group) => group.id
          )
        }
        // then remove the interest group bit
        delete editedPage.interestgroups

        await this.$store.dispatch('pages/update', {
          pageId: this.page.id,
          pageData: editedPage,
        })
      }
      this.$store.dispatch('pages/approve', this.page.id).then((page) => {
        // TODO: should drafts be deleted here? Or as part of approval
        try {
          this.$store.dispatch('pages/deleteDraft', page.id)
        } catch (err) {
          // just in case there is no draft
        }
        this.retrievePage()
      })
    },

    onClickReject() {
      /**
       * For this, we need to give a reason as to why we are rejecting
       */
      this.rejectModalOpen = true
      return
    },

    onClickViewPage() {
      const route = this.$router.resolve(`/content${this.page.page_url}`)
      window.open(route.href, '_blank')
    },
    validateSlug(slug) {
      if (!this.editMode && !slug) {
        return true
      }
      if (!slug) {
        this.$store.dispatch(
          'notifications/create',
          {
            body: `Please ensure the page slug isn't empty when editing`,
            type: 'danger',
          },
          { root: true }
        )
        return false
      }
      const count = (slug.match(/[^\w-]+/g) || []).length

      if (count >= 1) {
        this.$store.dispatch(
          'notifications/create',
          {
            body: `Please ensure the page slug contains no spaces or special characters`,
            type: 'danger',
          },
          { root: true }
        )
      }

      return count < 1
    },

    /**
     * @param {Boolean} fromDraft Whether we are coming from the save draft or publish action button
     */
    onClickPublish(fromDraft = false) {
      const vm = this
      vm.loading = true

      //only need certain fields
      const {
        id,
        status,
        primary_user_id,
        secondary_user_id,
        self,
        user_favourite,
        favourite_count,
        created_user,
        created_time,
        modified_user,
        modified_time,
        ...payload
      } = this.page

      payload.menu_sort = this.changeChildOrder && this.childMenuIds ? this.childMenuIds.join(',') : null

      // validate the slug
      if (!this.validateSlug(payload.slug)) {
        this.loading = false
        return
      }
      this.$store
        .dispatch('pages/create', payload)
        .then((page) => {
          /**
           * If there is no draft, we need to create one
           */
          if (!this.isDraft) {
            let interestgroup_ids = []
            if (page.interestgroups && page.interestgroups.length > 0) {
              interestgroup_ids = page.interestgroups.map((grp) => {
                return grp.id
              })
            }

            this.saveDraftPage(page)
              .then((draft) => {})
              .catch((err) => {
                this.$store.dispatch(
                  'notifications/create',
                  {
                    body: `There was a problem saving the draft`,
                    type: 'error',
                  },
                  { root: true }
                )
              })
          }
          vm.loading = false
          this.$store.commit('pageElements/setPageElements', [])
          // If we are a super admin, publish the page
          // Change this so that if we are saving a draft it doesn't auto-approve
          if (this.userCanApprove && !fromDraft) {
            this.$store.dispatch('pages/approve', page.id).then((resp) => {
              // here we need to delete a draft
              this.$store
                .dispatch('pages/deleteDraft', resp.id)
                .then((resp) => {
                  this.$router.push({
                    name: 'AdminContentPageEdit',
                    params: { pageId: page.id },
                  })
                })
                .catch((err) => {
                  this.$store.dispatch(
                    'notifications/create',
                    {
                      body: `There was a problem deleting the draft`,
                      type: 'error',
                    },
                    { root: true }
                  )
                  this.$router.push({
                    name: 'AdminContentPageEdit',
                    params: { pageId: page.id },
                  })
                })
            })
          } else {
            this.$router.push({
              name: 'AdminContentPageEdit',
              params: { pageId: page.id },
            })
          }
        })
        .catch((error) => {
          vm.loading = false
          if (error.meta.duplicate_page) {
            this.duplicatePage = error.meta.duplicate_page
          }
        })
    },

    /**
     * This will delete a draft
     * @argument reload Whether to reload page or not
     */
    onClickDeleteDraft(reload = true) {
      // reference the page id as this is how we query, rather then draft id
      const { page } = this

      this.$store.dispatch('createDialog', {
        confirm: () => {
          this.$store.dispatch('pages/deleteDraft', page.id).then((resp) => {
            //reset state
            this.$store.commit('pages/setCurrentPage', {})
            this.$store.commit('pageElements/setOriginalPageElements', [])
            this.$store.commit('pageElements/setPageElements', [])
            this.retrievePage(page.id)
            this.$store.dispatch(
              'notifications/create',
              {
                body: `The current draft has been deleted`,
                type: 'success',
              },
              { root: true }
            )
          })
        },
      })
    },

    onClickUpdate() {
      const { page } = this
      this.loading = true

      const pageElments = this.$store.getters['pageElements/forServer']
      const editedPage = JSON.parse(JSON.stringify(this.page))

      // add elements to the current page.
      editedPage.elements = pageElments

      // interest groups need to be just ids
      if (!editedPage.interestgroup_ids && editedPage.interestgroups) {
        editedPage.interestgroup_ids = editedPage.interestgroups.map(
          (group) => group.id
        )
      }
      // then remove the interest group bit
      delete editedPage.interestgroups

      if (!editedPage.title) {
        editedPage.title = 'Untitled Page'
      }

      /**
       * So, now we have to save a draft, and then post to the resubmit endpoint
       */

      const {
        title,
        slug,
        elements,
        parent_id,
        review_date,
        id,
        interestgroup_ids,
      } = editedPage

      this.saveDraftPage(editedPage)
        .then((draft) => {
          //only need certain fields
          const {
            id,
            status,
            primary_user_id,
            secondary_user_id,
            self,
            user_favourite,
            favourite_count,
            created_user,
            created_time,
            modified_user,
            modified_time,
            has_draft,
            parent_data,
            ...payload
          } = editedPage

          payload.menu_sort = this.childMenuIds
            ? this.childMenuIds.join(',')
            : null

          if (!this.validateSlug(payload.slug)) {
            this.loading = false
            return
          }

          this.$store
            .dispatch('pages/resubmit', { pageId: page.id, pageData: payload })
            .then((response) => {
              // merge the page and the draft?
              // make sure the ID is for the response, NOT the draft...
              this.page = {
                ...response[0],
                ...draft.data.data.draft[0],
                id: response[0].id,
              }
              // make sure we tell the page we are now editing a draft
              this.isDraft = true
              this.loading = false
            })
            .catch((error) => {
              this.loading = false
              if (error.meta && error.meta.duplicate_page) {
                this.duplicatePage = error.meta.duplicate_page
              }
            })
        })
        .catch((err) => {
          this.$store.dispatch(
            'notifications/create',
            {
              body: `There was a problem saving the draft`,
              type: 'error',
            },
            { root: true }
          )
        })
    },
  },

  computed: {
    ...mapState('pages', ['currentChildren']),
    isNew() {
      return this.page.status === 'new'
    },
    childMenuIds() {
      return this.currentChildren && this.currentChildren.length > 0
        ? this.currentChildren.map((child) => child.id)
        : null
    },
    isLapsedOrReview() {
      if (!this.originalReviewDate) {
        return false
      }
      return (
        this.$dayjs(this.originalReviewDate).unix() <
        this.$dayjs()
          .add(14, 'day')
          .unix()
      )
    },
    userCanSubmit() {
      return this.userHasPermission('add_edit_pages')
    },
    userCanApprove() {
      return this.userHasPermission('approve_pages')
    },
    elementsHaveChanged() {
      return this.$store.getters['pageElements/elementsHaveChanged']
    },

    isSuperAdmin() {
      return this.$store.state.auth.superAdmin
    },

    currentUserId() {
      return this.$store.state.auth.currentUser.id
    },

    page: {
      get() {
        return this.$store.state.pages.currentPage
      },
      set(value) {
        this.$store.commit('pages/setCurrentPage', value)
      },
    },
  },
}
</script>

<style lang="scss">
.p-page-create-edit {
  .c-section-block__header-main .c-form__group {
    margin: 0;
  }
}
</style>
